import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';
import { 
  Settings, Building2, Scale, Share2, Link2, MessageSquare, Image, 
  FileText, Search, Briefcase, Shield, Mail, Award, History, 
  Save, RotateCcw, Plus, Trash2, Loader2, Check, X, Eye, 
  ExternalLink, ChevronDown, ChevronUp, AlertTriangle, Upload, ImageIcon, Monitor, Tablet, Smartphone
} from 'lucide-react';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import MediaUpload from './MediaUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Format inline "Terracotta Mitonner" par classe (identité centralisée, pas de couleur libre)
const _Inline = Quill.import('blots/inline');
class TerracottaBlot extends _Inline {
  static formats() { return true; }
}
TerracottaBlot.blotName = 'terracotta';
TerracottaBlot.tagName = 'span';
TerracottaBlot.className = 'text-terracotta';
Quill.register('formats/terracotta', TerracottaBlot, true);

// Quill editor configuration
const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      [{ 'color': [] }, { 'background': [] }],
      ['terracotta'],
      [{ 'align': [] }],
      ['clean']
    ],
    handlers: {
      terracotta: function () {
        const range = this.quill.getSelection();
        if (!range || range.length === 0) return;
        const current = this.quill.getFormat(range);
        this.quill.format('terracotta', !current.terracotta);
      }
    }
  },
  clipboard: {
    matchVisual: false
  }
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'indent', 'link', 'image',
  'blockquote', 'code-block', 'color', 'background', 'align', 'terracotta'
];

// Tab configuration
const TABS = [
  { id: 'company', label: 'Entreprise', icon: Building2 },
  { id: 'media', label: 'Médias', icon: ImageIcon, highlight: true },
  { id: 'legal', label: 'Légal', icon: Scale },
  { id: 'social', label: 'Réseaux sociaux', icon: Share2 },
  { id: 'links', label: 'Liens externes', icon: Link2 },
  { id: 'communication', label: 'Communication', icon: MessageSquare },
  { id: 'content', label: 'Contenu', icon: FileText },
  { id: 'seo', label: 'SEO', icon: Search },
  { id: 'business', label: 'Métier', icon: Briefcase },
  { id: 'sap', label: 'SAP', icon: Shield },
  { id: 'email', label: 'Emails', icon: Mail },
  { id: 'logos', label: 'Logos & Labels', icon: Award },
];

// Media configuration for the Media tab
const MEDIA_ITEMS = [
  { 
    key: 'logo_url', 
    label: 'Logo principal', 
    description: 'Logo affiché dans le header de toutes les pages',
    recommended: 'PNG transparent, 200x80px minimum',
    usage: ['Header', 'Factures', 'Documents']
  },
  { 
    key: 'logo_white_url', 
    label: 'Logo blanc / inversé', 
    description: 'Version du logo pour les fonds sombres (footer)',
    recommended: 'PNG transparent blanc, 200x80px minimum',
    usage: ['Footer', 'Emails']
  },
  { 
    key: 'favicon_url', 
    label: 'Favicon', 
    description: 'Icône affichée dans l\'onglet du navigateur',
    recommended: 'PNG ou ICO, 32x32px ou 64x64px',
    usage: ['Onglet navigateur']
  },
  { 
    key: 'sap_logo_url', 
    label: 'Logo SAP', 
    description: 'Logo officiel Service à la Personne',
    recommended: 'PNG transparent, 150x150px',
    usage: ['Page SAP', 'Factures SAP']
  },
  { 
    key: 'hero_image_url', 
    label: 'Image d\'accueil (Hero)', 
    description: 'Grande image de la page d\'accueil',
    recommended: 'JPG ou WebP, 1920x800px minimum',
    usage: ['Landing page']
  },
  { 
    key: 'about_image_url', 
    label: 'Image "À propos"', 
    description: 'Image de la section À propos',
    recommended: 'JPG ou WebP, 800x600px',
    usage: ['Section À propos']
  },
  { 
    key: 'og_image_url', 
    label: 'Image Open Graph (SEO)', 
    description: 'Image affichée lors du partage sur les réseaux sociaux',
    recommended: 'JPG, 1200x630px (format Facebook/LinkedIn)',
    usage: ['Partage réseaux sociaux']
  },
  { 
    key: 'banner_image_url', 
    label: 'Bannière', 
    description: 'Image de bannière optionnelle',
    recommended: 'JPG ou WebP, 1920x400px',
    usage: ['Bannières promotionnelles']
  },
  { 
    key: 'email_logo_url', 
    label: 'Logo pour emails', 
    description: 'Logo utilisé dans les templates d\'emails (peut être différent)',
    recommended: 'PNG, 200x80px, fond transparent ou blanc',
    usage: ['Emails transactionnels']
  },
  { 
    key: 'pdf_logo_url', 
    label: 'Logo pour PDFs', 
    description: 'Logo utilisé dans les documents PDF (factures, fiches)',
    recommended: 'PNG haute résolution, 300x120px',
    usage: ['Factures', 'Fiches recettes']
  },
  { 
    key: 'default_recipe_image', 
    label: 'Image recette par défaut', 
    description: 'Image affichée quand une recette n\'a pas de photo',
    recommended: 'JPG ou WebP, 400x300px',
    usage: ['Cartes recettes']
  },
  { 
    key: 'vegetables_image_url', 
    label: 'Image légumes', 
    description: 'Image décorative de légumes pour la landing page',
    recommended: 'PNG transparent ou JPG, 600x400px',
    usage: ['Landing page']
  },
];

// Rich Text Editor Component
function RichTextEditor({ value, onChange, label, placeholder, testId }) {
  // État LOCAL synchrone pendant la frappe : évite le round-trip asynchrone via
  // l'état du parent (setConfig batché) qui faisait sauter le curseur / perdre le
  // texte tapé dans l'éditeur. On ne resynchronise que sur un vrai changement externe.
  const [local, setLocal] = useState(value || '');
  const lastEmitted = useRef(value || '');

  useEffect(() => {
    const incoming = value || '';
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      setLocal(incoming);
    }
  }, [value]);

  const handleChange = (html) => {
    lastEmitted.current = html;
    setLocal(html);
    if (onChange) onChange(html);
  };

  return (
    <div className="space-y-2" data-testid={testId || "config-rich-text-editor"}>
      {label && <Label className="font-semibold">{label}</Label>}
      <div className="border border-[#E3DED5] rounded-lg overflow-hidden bg-white">
        <ReactQuill
          theme="snow"
          value={local}
          onChange={handleChange}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder={placeholder}
          className="min-h-[200px]"
        />
      </div>
    </div>
  );
}

// Input Field Component
function Field({ label, type = 'text', value, onChange, placeholder, help, required, ...props }) {
  return (
    <div className="space-y-1">
      <Label className={`font-medium ${required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}`}>
        {label}
      </Label>
      {type === 'textarea' ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="input-default min-h-[100px]"
          {...props}
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="input-default"
          {...props}
        />
      )}
      {help && <p className="text-xs text-[#5C6A61]">{help}</p>}
    </div>
  );
}

// Toggle Switch Component
function Toggle({ label, value, onChange, help }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <Label className="font-medium">{label}</Label>
        {help && <p className="text-xs text-[#5C6A61]">{help}</p>}
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={value || false}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#A5BBA8]"></div>
      </label>
    </div>
  );
}

// Section Card Component
function SectionCard({ title, children, collapsible = false }) {
  const [isOpen, setIsOpen] = useState(true);
  
  return (
    <div className="card-default">
      <div 
        className={`flex items-center justify-between ${collapsible ? 'cursor-pointer' : ''}`}
        onClick={() => collapsible && setIsOpen(!isOpen)}
      >
        <h3 className="font-semibold text-[#2A312C]">{title}</h3>
        {collapsible && (
          isOpen ? <ChevronUp className="w-5 h-5 text-[#5C6A61]" /> : <ChevronDown className="w-5 h-5 text-[#5C6A61]" />
        )}
      </div>
      {isOpen && <div className="mt-4 space-y-4">{children}</div>}
    </div>
  );
}

// Main Component
export default function ConfigurationCenter() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  useEffect(() => {
    loadConfiguration();
  }, []);
  
  const loadConfiguration = async () => {
    try {
      const res = await adminApi.getConfiguration();
      setConfig(res.data);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading configuration:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };
  
  const loadHistory = async () => {
    try {
      const res = await adminApi.getConfigurationHistory(20);
      setHistory(res.data.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateConfiguration(config);
      toast.success('Configuration sauvegardée !');
      setHasChanges(false);
      loadHistory();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const handleReset = async () => {
    if (!window.confirm('Réinitialiser toute la configuration aux valeurs par défaut ? Cette action est irréversible.')) return;
    
    try {
      const res = await adminApi.resetConfiguration();
      setConfig(res.data);
      toast.success('Configuration réinitialisée');
      setHasChanges(false);
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };
  
  const handleRestore = async (version) => {
    if (!window.confirm(`Restaurer la configuration à la version ${version} ?`)) return;
    
    try {
      await adminApi.restoreConfiguration(version);
      await loadConfiguration();
      toast.success(`Configuration restaurée à la version ${version}`);
      setShowHistory(false);
    } catch (error) {
      toast.error('Erreur lors de la restauration');
    }
  };
  
  const updateConfig = useCallback((path, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current = newConfig;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      return newConfig;
    });
    setHasChanges(true);
  }, []);
  
  // Logo management
  const addLogo = async () => {
    const newLogo = {
      name: 'Nouveau logo',
      image_url: '',
      alt_text: '',
      display_location: ['footer'],
      is_active: true,
      order: (config.logos?.length || 0) + 1
    };
    
    try {
      const res = await adminApi.addConfigLogo(newLogo);
      setConfig(prev => ({
        ...prev,
        logos: [...(prev.logos || []), res.data.logo]
      }));
      toast.success('Logo ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du logo');
    }
  };
  
  const updateLogo = (index, field, value) => {
    const newLogos = [...(config.logos || [])];
    newLogos[index] = { ...newLogos[index], [field]: value };
    setConfig(prev => ({ ...prev, logos: newLogos }));
    setHasChanges(true);
  };
  
  const deleteLogo = async (logoId, index) => {
    if (!window.confirm('Supprimer ce logo ?')) return;
    
    try {
      await adminApi.deleteConfigLogo(logoId);
      setConfig(prev => ({
        ...prev,
        logos: prev.logos.filter((_, i) => i !== index)
      }));
      toast.success('Logo supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }
  
  if (!config) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-[#5C6A61]">Erreur lors du chargement de la configuration</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="configuration-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Centre de Configuration
          </h2>
          <p className="text-sm text-[#5C6A61]">
            Gérez tous les paramètres de Mitonner en un seul endroit
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge className="bg-orange-100 text-orange-800">Modifications non sauvegardées</Badge>
          )}
          <Dialog open={showHistory} onOpenChange={(open) => { setShowHistory(open); if (open) loadHistory(); }}>
            <DialogTrigger asChild>
              <button className="btn-outline flex items-center gap-2">
                <History className="w-4 h-4" />
                Historique
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Historique des modifications</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                {history.length === 0 ? (
                  <p className="text-center text-[#5C6A61] py-4">Aucun historique disponible</p>
                ) : (
                  history.map((entry) => (
                    <div key={entry.history_id} className="flex items-center justify-between p-3 border border-[#E3DED5] rounded-lg">
                      <div>
                        <p className="font-medium text-[#2A312C]">Version {entry.version}</p>
                        <p className="text-sm text-[#5C6A61]">
                          {new Date(entry.changed_at).toLocaleString('fr-FR')} par {entry.changed_by}
                        </p>
                        <p className="text-xs text-[#5C6A61]">{entry.changes_summary}</p>
                      </div>
                      <button
                        onClick={() => handleRestore(entry.version)}
                        className="btn-outline text-sm"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Restaurer
                      </button>
                    </div>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
          <button onClick={handleReset} className="btn-outline text-red-600 border-red-200 hover:bg-red-50">
            Réinitialiser
          </button>
          <button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
            className="btn-primary flex items-center gap-2"
            data-testid="config-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </button>
        </div>
      </div>
      
      {/* Version info */}
      <div className="text-sm text-[#5C6A61]">
        Version actuelle: {config.version || 1} 
        {config.updated_at && ` | Dernière modification: ${new Date(config.updated_at).toLocaleString('fr-FR')}`}
        {config.updated_by && ` par ${config.updated_by}`}
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 border-b border-[#E3DED5]">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`config-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-[#A5BBA8] text-white' 
                : 'bg-[#F9F6F0] text-[#5C6A61] hover:bg-[#E3DED5]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div className="space-y-6">
        {/* Section 1: Entreprise */}
        {activeTab === 'company' && (
          <>
            <SectionCard title="Informations générales">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nom de l'entreprise" value={config.company_name} onChange={(v) => updateConfig('company_name', v)} required />
                <Field label="Nom commercial / juridique" value={config.company_legal_name} onChange={(v) => updateConfig('company_legal_name', v)} />
                <Field label="Slogan" value={config.slogan} onChange={(v) => updateConfig('slogan', v)} />
                <Field label="Site internet" value={config.website} onChange={(v) => updateConfig('website', v)} type="url" />
              </div>
            </SectionCard>
            
            <SectionCard title="Coordonnées">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Adresse" value={config.address} onChange={(v) => updateConfig('address', v)} />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Code postal" value={config.postal_code} onChange={(v) => updateConfig('postal_code', v)} />
                  <Field label="Ville" value={config.city} onChange={(v) => updateConfig('city', v)} />
                </div>
                <Field label="Téléphone" value={config.phone} onChange={(v) => updateConfig('phone', v)} type="tel" />
                <Field label="Email" value={config.email} onChange={(v) => updateConfig('email', v)} type="email" />
                <Field label="Horaires d'ouverture" value={config.opening_hours} onChange={(v) => updateConfig('opening_hours', v)} />
                <Field label="Rayon d'intervention (km)" value={config.default_radius_km} onChange={(v) => updateConfig('default_radius_km', parseInt(v) || 30)} type="number" />
              </div>
            </SectionCard>
            
            <SectionCard title="Logo principal">
              <MediaUpload
                testIdPrefix="logo_url"
                value={config.logo_url}
                filename={config.media_filenames?.logo_url}
                onChange={(url, filename) => {
                  updateConfig('logo_url', url);
                  updateConfig('media_filenames.logo_url', filename || '');
                }}
              />
              <p className="text-xs text-[#5C6A61] mt-2 mb-4">Format recommandé: PNG transparent, 200x60px minimum</p>
              <Label className="text-sm mb-1 block">Favicon</Label>
              <MediaUpload
                testIdPrefix="favicon_url"
                value={config.favicon_url}
                filename={config.media_filenames?.favicon_url}
                onChange={(url, filename) => {
                  updateConfig('favicon_url', url);
                  updateConfig('media_filenames.favicon_url', filename || '');
                }}
              />
              <p className="text-xs text-[#5C6A61] mt-2">Format: .png 32x32px</p>
            </SectionCard>
          </>
        )}
        
        {/* Section 2: Légal */}
        {activeTab === 'legal' && (
          <>
            <SectionCard title="Identifiants légaux">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="SIREN" value={config.siren} onChange={(v) => updateConfig('siren', v)} placeholder="123 456 789" />
                <Field label="SIRET" value={config.siret} onChange={(v) => updateConfig('siret', v)} placeholder="123 456 789 00012" />
                <Field label="Numéro SAP" value={config.sap_number} onChange={(v) => updateConfig('sap_number', v)} placeholder="SAP102115565" />
                <Field label="TVA intracommunautaire" value={config.tva_number} onChange={(v) => updateConfig('tva_number', v)} placeholder="FR 12 345678901" />
                <Field label="Taux de TVA (%)" value={config.tva_rate} onChange={(v) => updateConfig('tva_rate', parseFloat(v) || 20)} type="number" />
                <Field label="RCS (ville)" value={config.rcs_city} onChange={(v) => updateConfig('rcs_city', v)} />
                <Field label="Code APE/NAF" value={config.ape_code} onChange={(v) => updateConfig('ape_code', v)} />
                <Field label="Capital social" value={config.capital} onChange={(v) => updateConfig('capital', v)} placeholder="10 000 €" />
              </div>
            </SectionCard>
            
            <SectionCard title="Documents légaux" collapsible>
              <div className="space-y-6">
                <RichTextEditor 
                  label="Mentions légales" 
                  value={config.legal_texts?.legal_mentions_html} 
                  onChange={(v) => updateConfig('legal_texts.legal_mentions_html', v)} 
                />
                <RichTextEditor 
                  label="Conditions Générales de Vente (CGV)" 
                  value={config.legal_texts?.cgv_html} 
                  onChange={(v) => updateConfig('legal_texts.cgv_html', v)} 
                />
                <RichTextEditor 
                  label="Conditions Générales d'Utilisation (CGU)" 
                  value={config.legal_texts?.cgu_html} 
                  onChange={(v) => updateConfig('legal_texts.cgu_html', v)} 
                />
                <RichTextEditor 
                  label="Politique de confidentialité" 
                  value={config.legal_texts?.privacy_policy_html} 
                  onChange={(v) => updateConfig('legal_texts.privacy_policy_html', v)} 
                />
                <RichTextEditor 
                  label="Politique des cookies" 
                  value={config.legal_texts?.cookies_policy_html} 
                  onChange={(v) => updateConfig('legal_texts.cookies_policy_html', v)} 
                />
              </div>
            </SectionCard>
          </>
        )}
        
        {/* Section 3: Réseaux sociaux */}
        {activeTab === 'social' && (
          <SectionCard title="Liens vers les réseaux sociaux">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Facebook" value={config.social_media?.facebook} onChange={(v) => updateConfig('social_media.facebook', v)} type="url" placeholder="https://facebook.com/..." />
              <Field label="Instagram" value={config.social_media?.instagram} onChange={(v) => updateConfig('social_media.instagram', v)} type="url" placeholder="https://instagram.com/..." />
              <Field label="LinkedIn" value={config.social_media?.linkedin} onChange={(v) => updateConfig('social_media.linkedin', v)} type="url" placeholder="https://linkedin.com/..." />
              <Field label="TikTok" value={config.social_media?.tiktok} onChange={(v) => updateConfig('social_media.tiktok', v)} type="url" placeholder="https://tiktok.com/..." />
              <Field label="YouTube" value={config.social_media?.youtube} onChange={(v) => updateConfig('social_media.youtube', v)} type="url" placeholder="https://youtube.com/..." />
              <Field label="Pinterest" value={config.social_media?.pinterest} onChange={(v) => updateConfig('social_media.pinterest', v)} type="url" placeholder="https://pinterest.com/..." />
              <Field label="Twitter/X" value={config.social_media?.twitter} onChange={(v) => updateConfig('social_media.twitter', v)} type="url" placeholder="https://twitter.com/..." />
            </div>
            <p className="text-sm text-[#5C6A61] mt-4">Les icônes des réseaux sociaux s&apos;afficheront automatiquement dans le footer si l&apos;URL est renseignée.</p>
          </SectionCard>
        )}
        
        {/* Section 4: Liens externes */}
        {activeTab === 'links' && (
          <SectionCard title="Liens externes officiels">
            <div className="space-y-4">
              <Field label="URSSAF (site principal)" value={config.external_links?.urssaf_main} onChange={(v) => updateConfig('external_links.urssaf_main', v)} type="url" />
              <Field label="URSSAF Avance Immédiate" value={config.external_links?.urssaf_avance_immediate} onChange={(v) => updateConfig('external_links.urssaf_avance_immediate', v)} type="url" />
              <Field label="Service Public" value={config.external_links?.service_public} onChange={(v) => updateConfig('external_links.service_public', v)} type="url" />
              <Field label="Services à la Personne (officiel)" value={config.external_links?.sap_official} onChange={(v) => updateConfig('external_links.sap_official', v)} type="url" />
            </div>
            <p className="text-sm text-[#5C6A61] mt-4">Ces liens sont utilisés sur la page SAP et dans les communications officielles.</p>
          </SectionCard>
        )}
        
        {/* Section 5: Communication */}
        {activeTab === 'communication' && (
          <SectionCard title="Paramètres de contact">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Email de contact" value={config.contact_email} onChange={(v) => updateConfig('contact_email', v)} type="email" />
              <Field label="Email du support" value={config.support_email} onChange={(v) => updateConfig('support_email', v)} type="email" />
              <Field label="Téléphone support" value={config.support_phone} onChange={(v) => updateConfig('support_phone', v)} type="tel" />
              <Field label="Destination formulaire de contact" value={config.contact_form_recipient} onChange={(v) => updateConfig('contact_form_recipient', v)} type="email" />
              <Field label="Horaires affichés" value={config.displayed_hours} onChange={(v) => updateConfig('displayed_hours', v)} placeholder="Du lundi au vendredi, 9h-18h" className="md:col-span-2" />
            </div>
          </SectionCard>
        )}
        
        {/* Section 6: MEDIAS - Gestion complète des médias */}
        {activeTab === 'media' && (
          <>
            {/* Logo Sizes Configuration */}
            <SectionCard title="Tailles du logo">
              <p className="text-sm text-[#5C6A61] mb-4">
                Configurez la taille du logo affichée selon le type d&apos;écran. La modification s&apos;applique immédiatement sur tout le site.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="card-default p-4 text-center">
                  <Monitor className="w-8 h-8 mx-auto text-[#A5BBA8] mb-2" />
                  <Label className="font-medium">Desktop</Label>
                  <p className="text-xs text-[#5C6A61] mb-2">Écrans ≥ 1024px</p>
                  <div className="flex items-center justify-center gap-2">
                    <input
                      type="number"
                      min="24"
                      max="120"
                      value={config.logo_height_desktop || 48}
                      onChange={(e) => updateConfig('logo_height_desktop', parseInt(e.target.value) || 48)}
                      className="input-default w-20 text-center"
                    />
                    <span className="text-sm text-[#5C6A61]">px</span>
                  </div>
                </div>
                <div className="card-default p-4 text-center">
                  <Tablet className="w-8 h-8 mx-auto text-[#A5BBA8] mb-2" />
                  <Label className="font-medium">Tablette</Label>
                  <p className="text-xs text-[#5C6A61] mb-2">Écrans 640-1023px</p>
                  <div className="flex items-center justify-center gap-2">
                    <input
                      type="number"
                      min="24"
                      max="100"
                      value={config.logo_height_tablet || 40}
                      onChange={(e) => updateConfig('logo_height_tablet', parseInt(e.target.value) || 40)}
                      className="input-default w-20 text-center"
                    />
                    <span className="text-sm text-[#5C6A61]">px</span>
                  </div>
                </div>
                <div className="card-default p-4 text-center">
                  <Smartphone className="w-8 h-8 mx-auto text-[#A5BBA8] mb-2" />
                  <Label className="font-medium">Mobile</Label>
                  <p className="text-xs text-[#5C6A61] mb-2">Écrans &lt; 640px</p>
                  <div className="flex items-center justify-center gap-2">
                    <input
                      type="number"
                      min="20"
                      max="80"
                      value={config.logo_height_mobile || 36}
                      onChange={(e) => updateConfig('logo_height_mobile', parseInt(e.target.value) || 36)}
                      className="input-default w-20 text-center"
                    />
                    <span className="text-sm text-[#5C6A61]">px</span>
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              {config.logo_url && (
                <div className="mt-6 p-4 bg-[#F9F6F0] rounded-lg">
                  <p className="text-sm font-medium text-[#2A312C] mb-3">Aperçu</p>
                  <div className="flex items-end gap-6 justify-center">
                    <div className="text-center">
                      <img 
                        src={config.logo_url} 
                        alt="Logo desktop" 
                        style={{ height: `${config.logo_height_desktop || 48}px` }}
                        className="object-contain mx-auto"
                      />
                      <p className="text-xs text-[#5C6A61] mt-1">Desktop</p>
                    </div>
                    <div className="text-center">
                      <img 
                        src={config.logo_url} 
                        alt="Logo tablet" 
                        style={{ height: `${config.logo_height_tablet || 40}px` }}
                        className="object-contain mx-auto"
                      />
                      <p className="text-xs text-[#5C6A61] mt-1">Tablette</p>
                    </div>
                    <div className="text-center">
                      <img 
                        src={config.logo_url} 
                        alt="Logo mobile" 
                        style={{ height: `${config.logo_height_mobile || 36}px` }}
                        className="object-contain mx-auto"
                      />
                      <p className="text-xs text-[#5C6A61] mt-1">Mobile</p>
                    </div>
                  </div>
                </div>
              )}
            </SectionCard>

            {/* Media Library */}
            <SectionCard title="Bibliothèque de médias">
              <p className="text-sm text-[#5C6A61] mb-4">
                Gérez tous les médias globaux du site. Chaque média est utilisé automatiquement partout où il est référencé.
              </p>
              
              <div className="space-y-6">
                {MEDIA_ITEMS.map((item) => (
                  <div key={item.key} className="border border-[#E3DED5] rounded-lg p-4">
                    <div className="flex flex-col gap-4">
                      {/* Info & Input */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <h4 className="font-semibold text-[#2A312C]">{item.label}</h4>
                            <p className="text-sm text-[#5C6A61]">{item.description}</p>
                          </div>
                        </div>

                        <MediaUpload
                          testIdPrefix={item.key}
                          value={config[item.key]}
                          filename={config.media_filenames?.[item.key]}
                          onChange={(url, filename) => {
                            updateConfig(item.key, url);
                            updateConfig(`media_filenames.${item.key}`, filename || '');
                          }}
                        />

                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="text-xs bg-[#F9F6F0] text-[#5C6A61] px-2 py-1 rounded">
                            {item.recommended}
                          </span>
                          {item.usage.map((u, i) => (
                            <span key={i} className="text-xs bg-[#A5BBA8]/10 text-[#5C6A61] px-2 py-1 rounded">
                              {u}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Conseil :</strong> Pour de meilleures performances, utilisez des images optimisées au format WebP ou JPEG compressé. 
                Les PNG sont recommandés pour les logos avec transparence.
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Note :</strong> Les couleurs du site (vert sauge, beige, terracotta) sont définies dans l&apos;identité visuelle et ne sont pas modifiables ici pour garantir la cohérence graphique.
              </p>
            </div>
          </>
        )}
        
        {/* Section 7: Contenu */}
        {activeTab === 'content' && (
          <>
            <SectionCard title="Page d'accueil">
              <div className="space-y-4">
                <Field label="Titre principal" data-testid="config-homepage_title" value={config.homepage_title} onChange={(v) => updateConfig('homepage_title', v)} />
                <Field label="Sous-titre" data-testid="config-homepage_subtitle" value={config.homepage_subtitle} onChange={(v) => updateConfig('homepage_subtitle', v)} type="textarea" />
              </div>
            </SectionCard>

            <SectionCard title="Section « À propos »">
              <div className="space-y-4">
                <Field label="Titre" data-testid="config-about_title" value={config.about_title} onChange={(v) => updateConfig('about_title', v)} />
                <div>
                  <Label>Paragraphe 1</Label>
                  <RichTextEditor testId="config-about_text1" value={config.about_text1} onChange={(v) => updateConfig('about_text1', v)} />
                </div>
                <div>
                  <Label>Paragraphe 2</Label>
                  <RichTextEditor testId="config-about_text2" value={config.about_text2} onChange={(v) => updateConfig('about_text2', v)} />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Bloc d'appel à l'action (bas de page d'accueil)">
              <div className="space-y-4">
                <Field label="Titre" data-testid="config-cta_title" value={config.cta_title} onChange={(v) => updateConfig('cta_title', v)} />
                <div>
                  <Label>Sous-titre</Label>
                  <RichTextEditor testId="config-cta_subtitle" value={config.cta_subtitle} onChange={(v) => updateConfig('cta_subtitle', v)} />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Points forts (section « Pourquoi choisir »)">
              <div className="grid sm:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="space-y-2 p-3 rounded-lg bg-[#F9F6F0]">
                    <Field label={`Atout ${n} — titre`} data-testid={`config-feature${n}_title`} value={config[`feature${n}_title`]} onChange={(v) => updateConfig(`feature${n}_title`, v)} />
                    <Field label={`Atout ${n} — description`} data-testid={`config-feature${n}_desc`} value={config[`feature${n}_desc`]} onChange={(v) => updateConfig(`feature${n}_desc`, v)} type="textarea" />
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Étapes (« Comment ça marche »)">
              <div className="grid sm:grid-cols-2 gap-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <div key={n} className="space-y-2 p-3 rounded-lg bg-[#F9F6F0]">
                    <Field label={`Étape ${n} — titre`} data-testid={`config-step${n}_title`} value={config[`step${n}_title`]} onChange={(v) => updateConfig(`step${n}_title`, v)} />
                    <Field label={`Étape ${n} — description`} data-testid={`config-step${n}_desc`} value={config[`step${n}_desc`]} onChange={(v) => updateConfig(`step${n}_desc`, v)} type="textarea" />
                  </div>
                ))}
              </div>
            </SectionCard>
            
            {/* Bloc WYSIWYG legacy retiré de l'administration : ces champs (homepage_intro_html,
                why_choose_us_html, how_it_works_html, faq_html, testimonials_html) n'étaient
                affichés nulle part côté public et doublonnaient les sections structurées
                « Pourquoi choisir » et « Comment ça marche ». Données conservées en base. */}
            
            <SectionCard title="Pied de page">
              <Field label="Texte du footer" value={config.footer_text} onChange={(v) => updateConfig('footer_text', v)} />
            </SectionCard>
          </>
        )}
        
        {/* Section 8: SEO */}
        {activeTab === 'seo' && (
          <>
            <SectionCard title="Métadonnées SEO">
              <div className="space-y-4">
                <Field label="Titre du site (balise title)" value={config.seo?.site_title} onChange={(v) => updateConfig('seo.site_title', v)} help="60 caractères max recommandés" />
                <Field label="Description (meta description)" value={config.seo?.meta_description} onChange={(v) => updateConfig('seo.meta_description', v)} type="textarea" help="160 caractères max recommandés" />
                <Field label="Mots-clés" value={config.seo?.meta_keywords} onChange={(v) => updateConfig('seo.meta_keywords', v)} help="Séparés par des virgules" />
              </div>
            </SectionCard>
            
            <SectionCard title="Open Graph (partage réseaux sociaux)">
              <div className="space-y-4">
                <Field label="Titre OG" value={config.seo?.og_title} onChange={(v) => updateConfig('seo.og_title', v)} placeholder="Laisser vide pour utiliser le titre SEO" />
                <Field label="Description OG" value={config.seo?.og_description} onChange={(v) => updateConfig('seo.og_description', v)} type="textarea" />
                <div>
                  <Label className="text-sm mb-1 block">Image de partage (1200x630px recommandé)</Label>
                  <MediaUpload
                    testIdPrefix="seo_og_image"
                    value={config.seo?.og_image}
                    filename={config.media_filenames?.seo_og_image}
                    onChange={(url, filename) => {
                      updateConfig('seo.og_image', url);
                      updateConfig('media_filenames.seo_og_image', filename || '');
                    }}
                  />
                </div>
              </div>
            </SectionCard>
            
            <SectionCard title="Analytics & Webmaster Tools">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Google Analytics ID" value={config.seo?.google_analytics_id} onChange={(v) => updateConfig('seo.google_analytics_id', v)} placeholder="G-XXXXXXXXXX" />
                <Field label="Google Search Console" value={config.seo?.google_search_console} onChange={(v) => updateConfig('seo.google_search_console', v)} placeholder="Code de vérification" />
              </div>
            </SectionCard>
          </>
        )}
        
        {/* Section 9: Métier */}
        {activeTab === 'business' && (
          <>
            <SectionCard title="Règles de réservation">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Délai minimum avant réservation (heures)" value={config.business_rules?.min_hours_before_booking} onChange={(v) => updateConfig('business_rules.min_hours_before_booking', parseInt(v) || 48)} type="number" />
                <Field label="Délai d'annulation (heures)" value={config.business_rules?.cancellation_hours} onChange={(v) => updateConfig('business_rules.cancellation_hours', parseInt(v) || 48)} type="number" />
                <Field label="Max réservations par jour" value={config.business_rules?.max_bookings_per_day} onChange={(v) => updateConfig('business_rules.max_bookings_per_day', parseInt(v) || 10)} type="number" />
                <Field label="Max réservations par employé/jour" value={config.business_rules?.max_bookings_per_employee_per_day} onChange={(v) => updateConfig('business_rules.max_bookings_per_employee_per_day', parseInt(v) || 2)} type="number" />
                <Field label="Durée créneau par défaut (heures)" value={config.business_rules?.default_slot_duration_hours} onChange={(v) => updateConfig('business_rules.default_slot_duration_hours', parseInt(v) || 4)} type="number" />
                <Field label="Temps de trajet buffer (minutes)" value={config.business_rules?.travel_time_buffer_minutes} onChange={(v) => updateConfig('business_rules.travel_time_buffer_minutes', parseInt(v) || 30)} type="number" />
                <Field label="Rayon d'intervention par défaut (km)" value={config.business_rules?.default_intervention_radius_km} onChange={(v) => updateConfig('business_rules.default_intervention_radius_km', parseInt(v) || 30)} type="number" />
              </div>
            </SectionCard>
            
            <SectionCard title="Activation des fonctionnalités">
              <div className="space-y-2">
                <Toggle label="Réservations activées" value={config.business_rules?.bookings_enabled} onChange={(v) => updateConfig('business_rules.bookings_enabled', v)} help="Permet aux clients de réserver" />
                <Toggle label="Paiements activés" value={config.business_rules?.payments_enabled} onChange={(v) => updateConfig('business_rules.payments_enabled', v)} help="Permet les paiements en ligne" />
                <Toggle label="Mode maintenance" value={config.business_rules?.maintenance_mode} onChange={(v) => updateConfig('business_rules.maintenance_mode', v)} help="Affiche une page de maintenance" />
                {config.business_rules?.maintenance_mode && (
                  <Field label="Message de maintenance" value={config.business_rules?.maintenance_message} onChange={(v) => updateConfig('business_rules.maintenance_message', v)} type="textarea" />
                )}
                <Toggle label="Mode simulation disponible" value={config.business_rules?.simulation_mode_enabled} onChange={(v) => updateConfig('business_rules.simulation_mode_enabled', v)} help="Permet le mode test admin" />
              </div>
            </SectionCard>
          </>
        )}
        
        {/* Section 10: SAP */}
        {activeTab === 'sap' && (
          <>
            <SectionCard title="Service à la Personne">
              <div className="space-y-4">
                <Toggle label="SAP activé" value={config.sap_enabled} onChange={(v) => updateConfig('sap_enabled', v)} help="Active les fonctionnalités SAP" />
                <Field label="Numéro SAP" value={config.sap_number} onChange={(v) => updateConfig('sap_number', v)} placeholder="SAP102115565" />
                <Field label="Texte de déclaration" value={config.sap_declaration_text} onChange={(v) => updateConfig('sap_declaration_text', v)} />
                <Field label="Taux de crédit d'impôt (%)" value={config.sap_credit_rate} onChange={(v) => updateConfig('sap_credit_rate', parseFloat(v) || 50)} type="number" />
                <Field label="Montant exemple (€)" value={config.sap_example_amount} onChange={(v) => updateConfig('sap_example_amount', parseFloat(v) || 200)} type="number" help="Pour les exemples de calcul (200€ → 100€)" />
              </div>
            </SectionCard>
            
            <SectionCard title="Logo SAP">
              <div className="space-y-4">
                <Toggle label="Afficher le logo SAP" value={config.sap_logo_enabled} onChange={(v) => updateConfig('sap_logo_enabled', v)} help="Affiche le logo à côté du numéro SAP" />
                <div>
                  <Label className="text-sm mb-1 block">Logo SAP</Label>
                  <MediaUpload
                    testIdPrefix="sap_logo_url"
                    value={config.sap_logo_url}
                    filename={config.media_filenames?.sap_logo_url}
                    onChange={(url, filename) => {
                      updateConfig('sap_logo_url', url);
                      updateConfig('media_filenames.sap_logo_url', filename || '');
                    }}
                  />
                </div>
                {config.sap_logo_url && (
                  <div className="flex items-center gap-4 p-4 bg-[#F9F6F0] rounded-lg">
                    <img src={config.sap_logo_url} alt="Logo SAP" className="h-12" />
                    <span className="text-[#5C6A61]">{config.sap_declaration_text} <strong>{config.sap_number}</strong></span>
                  </div>
                )}
              </div>
            </SectionCard>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Le contenu détaillé de la page SAP (textes, FAQ, exemples) se gère depuis l&apos;onglet &quot;Page SAP&quot; dans le menu de gauche.
              </p>
            </div>
          </>
        )}
        
        {/* Section 11: Emails */}
        {activeTab === 'email' && (
          <SectionCard title="Paramètres des emails">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nom de l'expéditeur" value={config.email_settings?.sender_name} onChange={(v) => updateConfig('email_settings.sender_name', v)} />
              <Field label="Email d'expédition" value={config.email_settings?.sender_email} onChange={(v) => updateConfig('email_settings.sender_email', v)} type="email" />
              <Field label="Email de réponse (reply-to)" value={config.email_settings?.reply_to} onChange={(v) => updateConfig('email_settings.reply_to', v)} type="email" />
              <Field label="Signature" value={config.email_settings?.signature_text} onChange={(v) => updateConfig('email_settings.signature_text', v)} />
              <Field label="Pied de page email" value={config.email_settings?.footer_text} onChange={(v) => updateConfig('email_settings.footer_text', v)} className="md:col-span-2" />
              <Field label="Texte de désinscription" value={config.email_settings?.unsubscribe_text} onChange={(v) => updateConfig('email_settings.unsubscribe_text', v)} className="md:col-span-2" />
            </div>
            <div className="mt-4 space-y-2">
              <Toggle label="Inclure le logo dans les emails" value={config.email_settings?.include_logo} onChange={(v) => updateConfig('email_settings.include_logo', v)} />
              <Toggle label="Inclure les liens réseaux sociaux" value={config.email_settings?.include_social_links} onChange={(v) => updateConfig('email_settings.include_social_links', v)} />
            </div>
          </SectionCard>
        )}
        
        {/* Section 12: Logos & Labels */}
        {activeTab === 'logos' && (
          <>
            <SectionCard title="Logos et certifications">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-[#5C6A61]">Gérez les logos affichés sur le site (partenaires, certifications, labels...)</p>
                <button onClick={addLogo} className="btn-outline text-sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter un logo
                </button>
              </div>
              
              <div className="space-y-4">
                {(config.logos || []).map((logo, idx) => (
                  <div key={logo.logo_id} className="p-4 border border-[#E3DED5] rounded-lg bg-[#F9F6F0]">
                    <div className="flex items-start gap-4">
                      {logo.image_url && (
                        <img src={logo.image_url} alt={logo.name} className="h-12 object-contain" />
                      )}
                      <div className="flex-1 grid md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={logo.name}
                          onChange={(e) => updateLogo(idx, 'name', e.target.value)}
                          className="input-default"
                          placeholder="Nom du logo"
                        />
                        <input
                          type="url"
                          value={logo.image_url}
                          onChange={(e) => updateLogo(idx, 'image_url', e.target.value)}
                          className="input-default"
                          placeholder="URL de l'image"
                        />
                        <input
                          type="text"
                          value={logo.alt_text}
                          onChange={(e) => updateLogo(idx, 'alt_text', e.target.value)}
                          className="input-default"
                          placeholder="Texte alternatif"
                        />
                        <input
                          type="url"
                          value={logo.link_url || ''}
                          onChange={(e) => updateLogo(idx, 'link_url', e.target.value)}
                          className="input-default"
                          placeholder="Lien (optionnel)"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={logo.is_active}
                            onChange={(e) => updateLogo(idx, 'is_active', e.target.checked)}
                            className="rounded"
                          />
                          Actif
                        </label>
                        <button
                          onClick={() => deleteLogo(logo.logo_id, idx)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label className="text-sm">Emplacements d&apos;affichage</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {['header', 'footer', 'sap_page', 'invoice', 'email'].map(loc => (
                          <label key={loc} className="flex items-center gap-1 text-sm">
                            <input
                              type="checkbox"
                              checked={(logo.display_location || []).includes(loc)}
                              onChange={(e) => {
                                const locations = logo.display_location || [];
                                if (e.target.checked) {
                                  updateLogo(idx, 'display_location', [...locations, loc]);
                                } else {
                                  updateLogo(idx, 'display_location', locations.filter(l => l !== loc));
                                }
                              }}
                              className="rounded"
                            />
                            {loc}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                
                {(config.logos || []).length === 0 && (
                  <div className="text-center py-8 text-[#5C6A61]">
                    Aucun logo ajouté. Cliquez sur &quot;Ajouter un logo&quot; pour commencer.
                  </div>
                )}
              </div>
            </SectionCard>
          </>
        )}
      </div>
      
      {/* Floating Save Button (on mobile) */}
      {hasChanges && (
        <div className="fixed bottom-4 right-4 md:hidden">
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="btn-primary shadow-lg flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </button>
        </div>
      )}
    </div>
  );
}
